import Vue from 'vue';
import clone from 'clone';
import { FeatureTypes } from '../services/constants';

const state = {
  user: null,
  userId: '',
  signedIn: false,
  userDetails: {
    displayName: '',
    bio: '',
    stripeid: '',
    subscriptionid: '',
    profilePictureSrc: '',
    group: '',
    features: undefined,
  },
  subscription: null,
  usage: {
    invoice: null,
    invoiceItems: null
  },
  plans: null,
  viewCount: 0,
  viewCap: 0,
  artworks: [],
  collections: [],
  artworkStoreUpdateRequired: true,
  collectionStoreUpdateRequired: true,
  godMode: false
};

const getters = {
  hasFeature(state) {
    return permission => {
      if (!Object.values(FeatureTypes).includes(permission)) {
        throw new Error(`Permission type ${permission} is not a valid permission type.`);
      }
      return state.userDetails.features.includes(permission);
    };
  },
  user() {
    return state.user;
  },
  userId() {
    return state.userId;
  },
  userDetails() {
    return state.userDetails;
  },
  subscription() {
    return state.subscription;
  },
  usage() {
    return state.usage;
  },
  periodEndFormatted() {
    return new Date(
      state.subscription.currentPeriodEnd * 1000
    ).toLocaleDateString();
  },
  stripeid() {
    return state.userDetails.stripeid;
  },
  signedIn() {
    return state.signedIn;
  },
  plans() {
    return state.plans;
  },
  allArtworks() {
    return state.artworks;
  },
  imageTargetArtworks() {
    return state.artworks.filter(
      a => a.artworkType === 'imageTarget' || a.artworkType === null
    );
  },
  worldTargetArtworks() {
    return state.artworks.filter(a => a.artworkType === 'worldTarget');
  },
  collections() {
    return state.collections;
  },
  artworkById: state => id => {
    return state.artworks.find(artwork => artwork.id === id);
  },
  collectionById: state => id => {
    return state.collections.find(collection => collection.id === id);
  },
  artworkStoreUpdateRequired() {
    return state.artworkStoreUpdateRequired;
  },
  collectionStoreUpdateRequired() {
    return state.collectionStoreUpdateRequired;
  },
  sortedArtworks: state => (artworkType, sortBy, sortDescending) => {
    let result;
    let artworksCopy;
    if (artworkType === 'imageTarget') {
      artworksCopy = state.artworks.filter(
        a => a.artworkType === 'imageTarget' || a.artworkType === null
      );
    } else if (artworkType === 'worldTarget') {
      artworksCopy = state.artworks.filter(
        a => a.artworkType === 'worldTarget'
      );
    }
    if (sortBy === 'title') {
      result = artworksCopy.sort((a, b) => {
        let nameA = a.name.toUpperCase();
        let nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    } else if (sortBy === 'views') {
      result = artworksCopy.sort((a, b) => {
        return b.viewsCount - a.viewsCount;
      });
    } else if (sortBy === 'dateCreated') {
      result = artworksCopy.sort((a, b) => {
        return new Date(a.created) - new Date(b.created);
      });
    } else if (sortBy === 'dateModified') {
      result = artworksCopy.sort((a, b) => {
        return new Date(a.modified) - new Date(b.modified);
      });
    } else if (sortBy === 'status') {
      const order = ['published', 'disabled', 'draft'];
      result = artworksCopy.sort((a, b) => {
        if (order.indexOf(a.status) < order.indexOf(b.status)) {
          return -1;
        }
        if (order.indexOf(a.status) > order.indexOf(b.status)) {
          return 1;
        }
        return 0;
      });
    }
    if (sortDescending) {
      result.reverse();
    }
    return result;
  },
  sortedCollections: state => (sortBy, sortDescending) => {
    let collectionsCopy = clone(state.collections);
    let result;
    if (sortBy === 'title') {
      result = collectionsCopy.sort((a, b) => {
        let nameA = a.title.toUpperCase();
        let nameB = b.title.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    } else if (sortBy === 'dateCreated') {
      result = collectionsCopy.sort((a, b) => {
        return new Date(a.created) - new Date(b.created);
      });
    } else if (sortBy === 'dateModified') {
      result = collectionsCopy.sort((a, b) => {
        return new Date(a.modified) - new Date(b.modified);
      });
    } else if (sortBy === 'status') {
      const order = ['published', 'disabled', 'draft'];
      result = collectionsCopy.sort((a, b) => {
        if (order.indexOf(a.status) < order.indexOf(b.status)) {
          return -1;
        }
        if (order.indexOf(a.status) > order.indexOf(b.status)) {
          return 1;
        }
        return 0;
      });
    }
    if (sortDescending) {
      result.reverse();
    }
    return result;
  },
  godMode() {
    return state.godMode;
  },
  viewCap() {
    return state.viewCap;
  },
  viewCount() {
    return state.viewCount;
  }
};

const mutations = {
  SET_SIGNED_IN(state, payload) {
    state.user = payload.user;
    state.signedIn = true;
  },
  SET_SIGNED_OUT(state) {
    state.user = null;
    state.userId = '';
    state.signedIn = false;
    state.userDetails.displayName = '';
    state.userDetails.bio = '';
    state.userDetails.stripeid = '';
    state.userDetails.subscriptionid = '';
    state.userDetails.profilePictureSrc = '';
    state.userDetails.group = '';
    state.userDetails.features = [FeatureTypes.PANELS],
    state.subscription = null;
    state.usage.invoice = null;
    state.usage.invoiceItems = null;
    state.plans = null;
    state.artworks = [];
    state.collections = [];
    state.viewCount = 0;
    state.viewCap = 0;
    state.artworkStoreUpdateRequired = true;
    state.collectionStoreUpdateRequired = true;
    state.godMode = false;
  },
  SET_USER_ID(state, payload) {
    state.userId = payload.userId;
  },
  SET_USER_DETAILS(state, payload) {
    state.userDetails.displayName = payload.displayName;
    state.userDetails.bio = payload.bio;
    state.userDetails.stripeid = payload.stripeid;
    state.userDetails.subscriptionid = payload.subscriptionid;
    state.userDetails.profilePictureSrc = payload.profilePictureSrc;
    state.userDetails.group = payload.group;
    state.userDetails.features = payload.features;
  },
  SET_STRIPE_ID(state, payload) {
    state.userDetails.stripeid = payload.stripeid;
  },
  SET_SUBSCRIPTION_ID(state, payload) {
    state.userDetails.subscriptionid = payload.subscriptionid;
  },
  SET_DISPLAY_NAME(state, payload) {
    state.userDetails.displayName = payload.displayName;
  },
  SET_PROFILE_PICTURE(state, payload) {
    state.userDetails.profilePictureSrc = payload.profilePictureSrc;
  },
  SET_BIO(state, payload) {
    state.userDetails.bio = payload.bio;
  },
  SET_GROUP(state, payload) {
    state.userDetails.group = payload.group;
  },
  SET_SUBSCRIPTION(state, payload) {
    state.subscription = payload.subscription;
  },
  SET_USAGE(state, payload) {
    if (payload.hasOwnProperty('invoice'))
      state.usage.invoice = payload.invoice;
    if (payload.hasOwnProperty('invoiceItems'))
      state.usage.invoiceItems = payload.invoiceItems;
  },
  SET_PLANS(state, payload) {
    state.plans = payload.plans;
  },
  SET_ARTWORKS(state, payload) {
    state.artworks = [...payload.artworks];
  },
  SET_COLLECTIONS(state, payload) {
    state.collections = payload.collections;
  },
  ADD_ARTWORK(state, payload) {
    state.artworks.push(payload.artwork);
  },
  UPDATE_ARTWORK(state, payload) {
    const { artwork: newArtwork } = payload;
    const oldArtwork = state.artworks.findIndex(
      artwork => artwork.id === newArtwork.id
    );
    if (oldArtwork.hasOwnProperty('collections'))
      newArtwork.collections = oldArtwork.collections;
    // Rebuild artwork array for reactivity
    state.artworks = [
      ...state.artworks.filter(artwork => artwork.id !== newArtwork.id),
      newArtwork
    ];
  },
  SET_PANELS_ARTWORK_UNCHANGED(state, payload) {
    const index = state.artworks.findIndex(artwork => artwork.id === payload.id);
    if (index === -1) {
      console.warn('[User] SET_PANELS_ARTWORK_UNCHANGED: artwork not found:', payload.id);
      return;
    }
    state.artworks[index].unchanged = payload.unchanged;
  },
  UPDATE_ARTWORK_FIELDS(state, payload) {
    let index = state.artworks.findIndex(artwork => artwork.id === payload.id);
    // Guard: artwork may not exist in local store (e.g., subscription update for another user's artwork)
    if (index === -1) {
      console.warn('[User] UPDATE_ARTWORK_FIELDS: artwork not found in local store:', payload.id);
      return;
    }
    if (
      payload.hasOwnProperty('viewsCount') &&
      payload.viewsCount !== undefined
    )
      state.artworks[index].viewsCount = payload.viewsCount;
    if (
      payload.hasOwnProperty('downloadsCount') &&
      payload.downloadsCount !== undefined
    )
      state.artworks[index].downloadsCount = payload.downloadsCount;
    if (payload.hasOwnProperty('name') && payload.name !== undefined)
      state.artworks[index].name = payload.name;
    if (
      payload.hasOwnProperty('description') &&
      payload.description !== undefined
    )
      state.artworks[index].description = payload.description;
    if (payload.hasOwnProperty('status') && payload.status !== undefined)
      state.artworks[index].status = payload.status;
    if (
      payload.hasOwnProperty('publishEnabled') &&
      payload.publishEnabled !== undefined
    )
      state.artworks[index].publishEnabled = payload.publishEnabled;
  },
  UPDATE_COLLECTION_FIELDS(state, payload) {
    let index = state.collections.findIndex(
      collection => collection.id === payload.id
    );
    // Guard: collection may not exist in local store
    if (index === -1) {
      console.warn('[User] UPDATE_COLLECTION_FIELDS: collection not found in local store:', payload.id);
      return;
    }
    if (
      payload.hasOwnProperty('viewsCount') &&
      payload.viewsCount !== undefined
    )
      state.collections[index].viewsCount = payload.viewsCount;
    if (
      payload.hasOwnProperty('downloadsCount') &&
      payload.downloadsCount !== undefined
    )
      state.collections[index].downloadsCount = payload.downloadsCount;
    if (payload.hasOwnProperty('title') && payload.title !== undefined)
      state.collections[index].title = payload.title;
    if (
      payload.hasOwnProperty('description') &&
      payload.description !== undefined
    )
      state.collections[index].description = payload.description;
    if (payload.hasOwnProperty('status') && payload.status !== undefined)
      state.collections[index].status = payload.status;
    if (
      payload.hasOwnProperty('publishEnabled') &&
      payload.publishEnabled !== undefined
    )
      state.collections[index].publishEnabled = payload.publishEnabled;
  },
  SET_ARTWORK_COLLECTIONS(state, payload) {
    let index = state.artworks.findIndex(
      artwork => artwork.id === payload.artworkid
    );
    if (index === -1) {
      console.warn('[User] SET_ARTWORK_COLLECTIONS: artwork not found:', payload.artworkid);
      return;
    }
    state.artworks[index].collections = payload.collections;
  },
  SET_COLLECTION_ARTWORKS(state, payload) {
    let index = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (index === -1) {
      console.warn('[User] SET_COLLECTION_ARTWORKS: collection not found:', payload.collectionid);
      return;
    }
    state.collections[index].containedArtworks = payload.containedArtworks;
    state.collections[index].artworks = payload.containedArtworks.length;
  },
  ADD_ARTWORK_TO_COLLECTION(state, payload) {
    let artworkIndex = state.artworks.findIndex(
      artwork => artwork.id === payload.artworkid
    );
    let collectionIndex = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (artworkIndex === -1 || collectionIndex === -1) {
      console.warn('[User] ADD_ARTWORK_TO_COLLECTION: artwork or collection not found:', payload);
      return;
    }
    state.artworks[artworkIndex].collections.push(
      state.collections[collectionIndex]
    );
    state.collections[collectionIndex].containedArtworks.push(
      state.artworks[artworkIndex]
    );
    state.collections[collectionIndex].artworks++;
    state.collections[collectionIndex].modified = Date.now();
  },
  REMOVE_ARTWORK_FROM_COLLECTION(state, payload) {
    let artworkIndex = state.artworks.findIndex(
      artwork => artwork.id === payload.artworkid
    );
    let collectionIndex = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (artworkIndex === -1 || collectionIndex === -1) {
      console.warn('[User] REMOVE_ARTWORK_FROM_COLLECTION: artwork or collection not found:', payload);
      return;
    }
    let collectionInArtworkIndex = state.artworks[
      artworkIndex
    ].collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (collectionInArtworkIndex !== -1) {
      state.artworks[artworkIndex].collections.splice(
        collectionInArtworkIndex,
        1
      );
    }

    let artworkInCollectionIndex = state.collections[
      collectionIndex
    ].containedArtworks.findIndex(artwork => artwork.id === payload.artworkid);
    if (artworkInCollectionIndex !== -1) {
      state.collections[collectionIndex].containedArtworks.splice(
        artworkInCollectionIndex,
        1
      );
    }
    state.collections[collectionIndex].artworks--;
    state.collections[collectionIndex].modified = Date.now();
  },
  SET_COLLECTION_COVER(state, payload) {
    let collectionIndex = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (collectionIndex === -1) {
      console.warn('[User] SET_COLLECTION_COVER: collection not found:', payload.collectionid);
      return;
    }
    // Vue.set, not plain assignment: a collection created in this session may not have
    // a `cover`/`checksum` key yet, and Vue 2 cannot track properties added afterwards.
    Vue.set(state.collections[collectionIndex], 'cover', payload.cover);
    // Keep the checksum in lockstep: it is the cover's cache-buster (see
    // coverImage.getCollectionCoverSrc). Without this the store keeps the previous
    // hash and a replaced cover keeps rendering the old image.
    if (Object.prototype.hasOwnProperty.call(payload, 'checksum')) {
      Vue.set(state.collections[collectionIndex], 'checksum', payload.checksum);
    }
  },
  DELETE_ARTWORK(state, payload) {
    let index = state.artworks.findIndex(
      artwork => artwork.id === payload.artworkID
    );

    let collectionsWithArtwork = state.collections.filter(collection => {
      let index = collection.containedArtworks.findIndex(
        artwork => artwork.id === payload.artworkID
      );
      return index !== -1;
    });

    collectionsWithArtwork.forEach(collection => {
      collection.artworks--;
      let artworkInCollectionIndex = collection.containedArtworks.findIndex(
        artwork => artwork.id === payload.artworkID
      );
      if (artworkInCollectionIndex !== -1) {
        collection.containedArtworks.splice(artworkInCollectionIndex, 1);
      }
    });
    if (index !== -1) {
      state.artworks.splice(index, 1);
    }
  },
  SET_ARTWORK_STORE_UPDATE_REQUIRED(state, payload) {
    state.artworkStoreUpdateRequired = payload.artworkStoreUpdateRequired;
  },
  SET_COLLECTION_STORE_UPDATE_REQUIRED(state, payload) {
    state.collectionStoreUpdateRequired = payload.collectionStoreUpdateRequired;
  },
  ADD_COLLECTION(state, payload) {
    state.collections.push(payload.collection);
  },
  UPDATE_COLLECTION(state, payload) {
    let index = state.collections.findIndex(
      collection => collection.id === payload.collection.id
    );
    if (index === -1) {
      console.warn('[User] UPDATE_COLLECTION: collection not found:', payload.collection.id);
      return;
    }
    state.collections[index] = payload.collection;
    state.collections[index].modified = Date.now();
  },
  SET_COLLECTION_COVER_IMAGE(state, payload) {
    let index = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (index === -1) {
      console.warn('[User] SET_COLLECTION_COVER_IMAGE: collection not found:', payload.collectionid);
      return;
    }
    state.collections[index].cover = payload.cover;
  },
  DELETE_COLLECTION(state, payload) {
    let index = state.collections.findIndex(
      collection => collection.id === payload.collectionid
    );
    if (index !== -1) {
      state.collections.splice(index, 1);
    }
  },
  SET_VIEW_COUNT(state, payload) {
    state.viewCount = payload.viewCount;
  },
  SET_VIEW_CAP(state, payload) {
    state.viewCap = payload.viewCap;
  },
  ACTIVATE_GOD_MODE(state) {
    state.godMode = true;
  }
};

const actions = {};

export default {
  state,
  getters,
  mutations,
  actions
};
